<template>
  <!------------------------------------------
    Map for the print version of the congressional state profiles.
  -------------------------------------------->
  <div id="congr-state-print-map">
    <div>
      <MapTemplate :showLayerPanel="false" :mapContainerId="mapContainerId" :loading="loading" :mapHeight="mapHeight" />
    </div>
  </div>
</template>

<script>
import MapTemplate from '@/components/map/MapTemplate'
import bboxes from '@/js/bboxes.js'
import states from '@/js/stFips.js'
import mapboxgl from "mapbox-gl"

let map

export default {
  name: 'StateProfilesPrintMap',

  components: { MapTemplate },

  props: {
    selectedState: { type: String, required: true },
    colorPerServArea: { type: Array, required: true },
    officesPerGrantee: { type: Object, required: true },
    mapboxStyle: { type: String, required: false, default: "mapbox://styles/lsc-odga/cl8sv2oq8003i15p90nywwsu6?optimize=true" },
    interactive: { type: Boolean, required: false, default: true },
    mapHeight: { type: String, required: false, default: '18rem' },
  },

  data () {
    return {
      loaded: false,
      newBBox: null, // When changing the bounding boxes, set the new bounding box to be newBBox to automatically re-fit the map
      defaultBoundsPadding: 25,
      accessToken: `${process.env.VUE_APP_MAPBOX_TOKEN}`,
      map: null,
      defaultBBox: [-124.736342, 24.521208, -66.945392, 49.382808], // contiguous US (used if no bbox provided)
      allowScrollZoom: false,
      loading: false,
      mapContainerId: 'map-container',
      layers: [
        { 
          tilesetId: 'lsc-odga.2024_bf_general_v2',
          loadOnStart: true,
          layerDefs: {
            'id': '2024_bf_general_v2-fill',
            'type': 'fill',
            'source': `2024_bf_general_v2-fill-src`,
            'source-layer': '2024_bf_general_v2',
            'paint': {
              'fill-opacity': 0.7,
              'fill-color': '#165991',
              'fill-outline-color': 'white'
            }
          }
        },
        { 
          tilesetId: 'lsc-odga.2024_bf_general_v2',
          loadOnStart: true,
          layerDefs: {
            'id': '2024_bf_general_v2',
            'type': 'line',
            'source': `2024_bf_general_v2-src`,
            'source-layer': '2024_bf_general_v2',
            'paint': {
              'line-width': 0.5,
              'line-color': '#f6f6f6'
            }
          }
        }
      ]
    }
  },

  computed: {
    bbox () { return this.newBBox === null || this.newBBox === undefined ? this.defaultBBox : this.newBBox },
    paddingOrDefault () { return this.padding === null || this.padding === undefined ? this.defaultBoundsPadding : this.padding },
    selectedStateAbbrev () { 
      if (this.selectedState !== '' && this.selectedState !== null && this.selectedState !== undefined) {
        return states.filter(x => x.fips === String(this.selectedState))[0].st
      } else {
        return ''
      }
    },
    filterStateField () { 
      return this.selectedStateAbbrev !== '' ? ["==", "st_abbrev", this.selectedStateAbbrev] : ['has', 'rno'] 
    },
    colorExpression () {
      let exp = ['match', ['get', 'serv_area']]
      this.colorPerServArea.forEach(function (d) {
        exp.push(
          d['area'],
          d['color']
        )
      })
      exp.push("#aaa") // default if no match
      return exp
    }
  },

  created () {
  },
  
  async mounted () {
    // Initialize the map
    mapboxgl.accessToken = this.accessToken
    this.$nextTick(() => {
      this.mapSetup()
    })
    this.newBBox = bboxes.states[this.selectedState]
    if (this.selectedState === '60') { // American Samoa
      this.newBBox = [-170.8762,-14.395,-169.189,-14.1262]
    }
  },

  methods: {
    mapSetup: function() { 
      this.loading = true
      let padding = this.paddingOrDefault,
          allowScrollZoom = this.allowScrollZoom,
          mapContainerId = this.mapContainerId,
          component = this

      map = new mapboxgl.Map({
        container: mapContainerId,
        style: this.mapboxStyle,
        interactive: this.interactive,
        preserveDrawingBuffer: true
      })

      map.fitBounds( component.bbox, { padding: padding, duration:0 })

      if (allowScrollZoom === false) {
        map.scrollZoom.disable()
      }

      // ZOOM CONTROLS
      map.addControl(new mapboxgl.FullscreenControl({container: document.querySelector('#' + mapContainerId)}), 'top-left')
      const nav = new mapboxgl.NavigationControl({
        visualizePitch: true
      })
      map.addControl(nav, 'top-left')

      // LAYERS
      map.on('load', function () {
        if (component.layers.length > 0) {
          component.initLayers()
        }

        setTimeout(() => { 
          component.loading = false 
          component.loaded = true
        }, 100)
      })
    },

    async initLayers () {
      this.layers.forEach(layer => {
        if (layer.loadOnStart === true) {
          map.addSource(`${layer.layerDefs['source']}`, {
            type: 'vector',
            url: `mapbox://${layer.tilesetId}`
          })
          map.addLayer(layer.layerDefs)
        }
      })
      this.filterLayers()
    },

    fitBounds (bbox) {
      if (map) {
        let padding = this.paddingOrDefault
        map.fitBounds( bbox, { padding: padding, duration:0 })
      }
    },

    filterLayers () {
      const colorExp = this.colorExpression

      if (map) {
        this.layers.forEach(layer => {
          layer.layerDefs['filter'] = this.filterStateField
          try {
            map.setFilter(layer.layerDefs['id'], this.filterStateField)
            
            if (layer.layerDefs['id'] === '2024_bf_general_v2-fill') {
              map.setPaintProperty(layer.layerDefs['id'], 'fill-color', colorExp)
            }
          } catch (err) {
            console.log(err)
          }
        })
      }
    },

    hideMapElementsForPrint () {
      const elements = document.querySelectorAll('.mapboxgl-control-container');
      elements.forEach(el => { el.remove() })
    },

    async addOffices () {

      const numGrantees = Object.keys(this.officesPerGrantee).length
      const isSingleGrantee = numGrantees === 1 ? true : false
      const branchDotSize = isSingleGrantee === true ? 0.5 : 0
            
      for (const rno of Object.keys(this.officesPerGrantee)) {

        let o = this.officesPerGrantee[rno]
        let s = `offices_${rno}`

        // Remove the layer if already exists from previous draw
        const alreadyExists = map.getLayer(s)
        if(typeof alreadyExists !== 'undefined') {
          map.removeLayer(s).removeSource(s);
        }

        if (o !== null && o !== undefined) {
          map.addSource(s, {
            'type': 'geojson',
            'data': o
          })

          map.addLayer({
            'id': s,
            'type': 'symbol',
            'source': s,
            'layout': {
              'icon-image': ['get', 'type'],
              'icon-size': ['match', ['get', 'type'], 'Main', 1.1, branchDotSize],
              'icon-allow-overlap': true
            }
          })
        }
      }

    },

    async removeOffices (rnos) {
      // Remove offices from prior map loads
      for (const rno of rnos) {
        let s = `offices_${rno}`
        const alreadyExists = map.getLayer(s)
        if(typeof alreadyExists !== 'undefined') {
          map.removeLayer(s).removeSource(s)
        }
      }
    }
  },

  watch: {
    newBBox(to) {
      this.fitBounds(to)
    },
    loaded (to) {
      if (to === true) {
        this.hideMapElementsForPrint()
        
        if (Object.keys(this.officesPerGrantee).length > 0) {
          this.addOffices()
        }
      }
    },
    officesPerGrantee (to, from) {
      if (this.loaded === true) {
        this.removeOffices(Object.keys(from))
        this.addOffices()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/scss/vars.scss';

</style>